<template>
  <div
    v-if="environment !== 'prod'"
    :class="[
      'env-badge',
      environment,
      { 'env-badge--interactive': canOpenFeatureFlags },
    ]"
    @click="handleBadgeClick"
  >
    <span class="kds-typography-title-xlarge">
      {{ environment.toUpperCase() }}
    </span>
  </div>
  <DevModal
    v-if="canOpenFeatureFlags"
    ref="devModal"
    :user-email="userEmail"
  />
  <BaseButton
    v-if="canOpenFeatureFlags && showFillButton"
    class="env-badge-fill-btn"
    :text="isAtTop ? 'Autofill' : 'A'"
    size="small"
    @click="handleFillClick"
  />
</template>

<script setup>
import env from '@shared/env.js'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import BaseButton from '@shared/components/BaseButton.vue'
import DevModal from '@shared/components/DevModal.vue'
import useFeatureFlagStore from '@shared/stores/useFeatureFlagStore'
import useAutofillStore from '@shared/stores/useAutofillStore'

const environment = computed(() => env.VITE_MODE || 'dev')
const featureFlagStore = useFeatureFlagStore()
const canOpenFeatureFlags = computed(() => featureFlagStore.canOverrideFlags)
const devModal = ref(null)
const route = useRoute()
const autofillStore = computed(() =>
  canOpenFeatureFlags.value ? useAutofillStore() : null,
)
const showFillButton = computed(
  () => Boolean(autofillStore.value?.enabled) && route.name === 'sign-up',
)

defineProps({
  userEmail: {
    type: String,
    default: '',
  },
})

const handleBadgeClick = () => devModal.value?.open()

const isAtTop = ref(window.scrollY === 0)
const onScroll = () => {
  isAtTop.value = window.scrollY === 0
}

watch(
  showFillButton,
  (enabled, _previous, onCleanup) => {
    if (!enabled) return

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    onCleanup(() => window.removeEventListener('scroll', onScroll))
  },
  { immediate: true },
)

const handleFillClick = async () => {
  if (!autofillStore.value) return

  const { fillForm, submitForm } =
    await import('@shared/helpers/autofillHelper')
  fillForm(autofillStore.value.fields)
  submitForm()
}
</script>

<style lang="scss" scoped>
.env-badge {
  background-color: $kds-color-background-yellow-accent;
  box-shadow: 0 0 6px 2px rgba(0, 0, 0, 0.15);
  height: 128px;
  left: -64px;
  pointer-events: none;
  position: fixed;
  top: calc(env(safe-area-inset-top) - 64px);
  transform: rotate(-45deg) scale(0.5);
  width: 128px;
  z-index: 11;

  &.stage {
    background-color: $kds-color-background-green-accent;
  }

  span {
    bottom: 0;
    position: absolute;
    text-align: center;
    width: 100%;
  }

  &--interactive {
    cursor: pointer;
    pointer-events: auto;
  }
}

.env-badge-fill-btn {
  bottom: $kds-space-2x;
  box-shadow: 0 0 0 4px $kds-color-background-yellow-accent;
  position: fixed;
  right: $kds-space-2x;
  width: fit-content;
  z-index: 11;
}
</style>
